import { Divider, Flex, Heading, Image, Link, View } from '@adobe/react-spectrum';
import WebPage from '@spectrum-icons/workflow/WebPage';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import { TraderInformation, WithSkeleton } from '@exchange-frontends/components';

import { NO_DETAILS_MESSAGE } from '../../../../components/TraderInformation';
import { formatWebsite } from '../../../../utils';

/**
 * ProfileInfo rounded image container
 * @memberof ProfileInfo.Header
 * @type {styled-component}
 */
const RoundedImageContainer = styled.div`
  img {
    border-radius: var(--spectrum-global-dimension-size-50);
    width: auto;
  }
`;

/**
 * ProfileInfo body container
 * @memberof ProfileInfo.Body
 * @type {styled-component}
 */
const ProfileInfoBody = styled.div`
  color: var(--spectrum-global-color-gray-800);
`;

/**
 * Publisher Profile info component
 * @component
 * @category Experience Cloud Details
 * @subcategory Details / Publisher Profile
 * @prop {('ProfileInfo.Header'|'ProfileInfo.Body'|'ProfileInfo.Footer')} children - ProfileInfo sub-components
 */
const ProfileInfo = ({ children }) => {
  const header = React.Children.map(children, (child) => (child.type.displayName === 'Header' ? child : null));
  const body = React.Children.map(children, (child) => (child.type.displayName === 'Body' ? child : null));
  const footer = React.Children.map(children, (child) => (child.type.displayName === 'Footer' ? child : null));
  const shouldRenderBody = useMemo(() => body[0].props.children !== null || body[0].props.loading === true, [body]);
  const shouldRenderFooter = useMemo(() => footer[0].props.website || footer[0].props.loading === true, [footer]);
  return (
    <View
      backgroundColor="static-white"
      borderRadius="regular"
      paddingX="size-400"
      paddingY="size-300"
      borderWidth="thin"
      borderColor="gray-200"
      marginBottom="size-500"
    >
      {header}
      {shouldRenderBody && <Divider size="S" marginY="size-250" />}
      {body}
      {shouldRenderFooter && <Divider size="S" marginY="size-250" />}
      {footer}
    </View>
  );
};

/**
 * Publisher ProfileInfo Header sub-component
 * @param {object} props
 * @param {string} [props.icon] - url of AppCard icon
 * @param {string} [props.name] - name of the Publisher
 * @param {import('@action-types/ecPublisher').EcPublisherInfoResponse['partnerLevel']} [props.level] - publisher partner level
 * @param {boolean} [props.loading]
 */
ProfileInfo.Header = ({ icon, name, level, loading }) => (
  <Flex justifyContent="space-between" alignItems="center" rowGap="size-200" wrap>
    <Flex alignItems="center" flex="1">
      <RoundedImageContainer>
        <WithSkeleton shouldRender={!loading} width={48} height={48} circle inline>
          {icon ? <Image src={icon} height="size-600" objectFit="contain" alt="" data-testid="Publisher Logo" /> : null}
        </WithSkeleton>
      </RoundedImageContainer>

      <Heading level={2} marginX="size-150" flex="1" marginY="0">
        <WithSkeleton shouldRender={!loading} inline style={{ maxWidth: '500px', minWidth: '150px' }}>
          {name}
        </WithSkeleton>
      </Heading>
    </Flex>
    <WithSkeleton shouldRender={!loading} height={50} width={160} inline>
      {level && (
        <Image
          src={level}
          height="size-500"
          objectFit="contain"
          maxWidth="250px"
          data-testid="Partner Level"
          alt={`${level} Technology Partner`}
        />
      )}
    </WithSkeleton>
  </Flex>
);
ProfileInfo.Header.displayName = 'Header';

/**
 * Publisher ProfileInfo Body sub-component
 * @param {object} props
 * @param {React.ReactNode} [props.children] - content of the ProfileInfo body
 * @param {boolean} [props.loading] - indicates if the data is loading
 */
ProfileInfo.Body = ({ children, loading }) => (
  <WithSkeleton shouldRender={!loading} count={2}>
    {children ? <ProfileInfoBody>{children}</ProfileInfoBody> : null}
  </WithSkeleton>
);
ProfileInfo.Body.displayName = 'Body';

/**
 * Publisher ProfileInfo Footer sub-component
 * @param {Object} props
 * @param {string} [props.website] - url of web page
 * @param {import('@action-types/ecPublisher').PublisherContactDetails} [props.contactDetails] - contact details of the publisher
 * @param {boolean} [props.loading] - indicates if the data is loading
 * @param {boolean} [props.showTraderInfo] - indicates if the trader info should be shown
 * @param {boolean} [props.showHiddenListingsDisclaimer] - indicates if the hidden listings disclaimer should be shown
 */
ProfileInfo.Footer = ({ website, contactDetails, loading, showTraderInfo, showHiddenListingsDisclaimer }) => {
  const fixedWebsite = formatWebsite(website);

  const shouldShow = website || showTraderInfo;

  if (!shouldShow && !loading) {
    return null;
  }

  let noDetailsMessage = NO_DETAILS_MESSAGE;

  if (showHiddenListingsDisclaimer) {
    noDetailsMessage += ' Some of their listings may not be visible.';
  }

  return (
    <Flex gap="size-300" wrap>
      <Flex direction="column" flex="1">
        <WithSkeleton shouldRender={!loading} width="100%" fullWidthContainer style={{ maxWidth: '500px' }}>
          {website && (
            <Flex columnGap="size-100" alignItems="center" flex="1">
              <WebPage size="S" />
              <Link href={fixedWebsite} target="_blank" rel="noopener noreferrer">
                {fixedWebsite}
              </Link>
            </Flex>
          )}
          {showTraderInfo && (
            <>
              <Divider size="S" marginY="size-250" />
              <TraderInformation contactDetails={contactDetails} noDetailsMessage={noDetailsMessage} />
            </>
          )}
        </WithSkeleton>
      </Flex>
    </Flex>
  );
};
ProfileInfo.Footer.displayName = 'Footer';

export default ProfileInfo;
